/**
 * Messenger glyphs.
 *
 * Kept apart from the application icon set: these are the specific marks a
 * messenger interface needs — ticks, tails, waveform controls — drawn to match
 * the density of this layout. Both sets are inline SVG for the same reason
 * (ADR 0005: no third-party code on a conversation page, and the CSP forbids a
 * remote origin).
 */

export type Glyph =
  | 'search'
  | 'menu'
  | 'new-chat'
  | 'attach'
  | 'emoji'
  | 'mic'
  | 'send'
  | 'back'
  | 'close'
  | 'check'
  | 'check-double'
  | 'clock'
  | 'warning'
  | 'play'
  | 'pause'
  | 'image'
  | 'camera'
  | 'document'
  | 'video'
  | 'download'
  | 'lock'
  | 'flag'
  | 'info'
  | 'settings'
  | 'logout'
  | 'palette'
  | 'user'
  | 'translate'
  | 'trash'
  | 'link'
  | 'copy'
  | 'chat-empty';

const P: Record<Glyph, JSX.Element> = {
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>
  ),
  menu: (
    <>
      <circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none" />
    </>
  ),
  'new-chat': (
    <>
      <path d="M21 11.5a8.4 8.4 0 0 1-12.8 7.5L3 20.5l1.5-4.2A8.4 8.4 0 1 1 21 11.5Z" />
      <path d="M12 8.5v6M9 11.5h6" />
    </>
  ),
  attach: (
    <path d="M20.4 11.7 12 20a5.7 5.7 0 0 1-8-8l8.6-8.6a3.8 3.8 0 0 1 5.4 5.4l-8.6 8.6a1.9 1.9 0 1 1-2.7-2.7l7.9-7.9" />
  ),
  emoji: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
      <circle cx="9" cy="9.8" r="1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="9.8" r="1" fill="currentColor" stroke="none" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="2.5" width="6" height="11.5" rx="3" />
      <path d="M19 11v.5a7 7 0 0 1-14 0V11M12 18.5V22" />
    </>
  ),
  send: <path d="M2.5 12 21.5 3l-4 9 4 9L2.5 12Zm0 0h9" />,
  back: <path d="M19 12H5m0 0 6.5 6.5M5 12l6.5-6.5" />,
  close: <path d="M18 6 6 18M6 6l12 12" />,
  check: <path d="m4.5 12.5 5 5 10-11" />,
  'check-double': (
    <>
      <path d="m1.5 12.5 4.5 4.5 8.5-9.5" />
      <path d="m9 12.5 4 4.5 8.5-9.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v5l3 1.8" />
    </>
  ),
  warning: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5v5.5M12 16.3h.01" />
    </>
  ),
  play: <path d="M8 5.5v13l11-6.5-11-6.5Z" fill="currentColor" stroke="none" />,
  pause: (
    <>
      <rect x="7" y="5" width="3.6" height="14" rx="1" fill="currentColor" stroke="none" />
      <rect x="13.4" y="5" width="3.6" height="14" rx="1" fill="currentColor" stroke="none" />
    </>
  ),
  image: (
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <circle cx="8.5" cy="10" r="1.6" />
      <path d="m3.5 17 5-5 4.5 4.5 3-3 4.5 4.5" />
    </>
  ),
  camera: (
    <>
      <path d="M4 7.5h3l1.5-2.5h7L17 7.5h3a1.5 1.5 0 0 1 1.5 1.5v8.5a1.5 1.5 0 0 1-1.5 1.5H4a1.5 1.5 0 0 1-1.5-1.5V9A1.5 1.5 0 0 1 4 7.5Z" />
      <circle cx="12" cy="13" r="3.6" />
    </>
  ),
  document: (
    <>
      <path d="M14 3H7.5A2 2 0 0 0 5.5 5v14a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V7.5L14 3Z" />
      <path d="M14 3v4.5h4.5" />
    </>
  ),
  video: (
    <>
      <rect x="2.5" y="6.5" width="13" height="11" rx="2" />
      <path d="m21.5 8.5-6 3.5 6 3.5v-7Z" />
    </>
  ),
  download: (
    <path d="M12 3.5v11m0 0 4-4m-4 4-4-4M4.5 17v2a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-2" />
  ),
  lock: (
    <>
      <rect x="4.5" y="10" width="15" height="10.5" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </>
  ),
  flag: <path d="M4.5 21V4m0 0 6.5 2 6.5-2-1.5 6 1.5 6-6.5-2-6.5 2" />,
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 16.5V11M12 8h.01" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 19.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z" />
    </>
  ),
  logout: (
    <path d="M15 17v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v2M11 12h10m0 0-3.5-3.5M21 12l-3.5 3.5" />
  ),
  palette: (
    <>
      <path d="M12 21a9 9 0 1 1 9-9c0 1.7-1.3 3-3 3h-1.5a2 2 0 0 0-1.4 3.4c.4.4.4 1.1 0 1.5-.6.6-1.6 1.1-3.1 1.1Z" />
      <circle cx="7.5" cy="10.5" r="1" />
      <circle cx="12" cy="7.5" r="1" />
      <circle cx="16.5" cy="10.5" r="1" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
    </>
  ),
  translate: (
    <>
      <path d="M3 6h9M7.5 4v2M10 6c0 4-3 7-7 7" />
      <path d="M5.5 10.5c1.7 2.2 4 3.5 6 4" />
      <path d="m12.5 21 4.5-11 4.5 11M14.4 17.5h6.2" />
    </>
  ),
  trash: (
    <>
      <path d="M3.5 6.5h17M9 6.5v-2a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      <path d="M18.5 6.5v13a2 2 0 0 1-2 2h-9a2 2 0 0 1-2-2v-13" />
    </>
  ),
  link: (
    <>
      <path d="M10 13.5a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7L11.8 5.5" />
      <path d="M14 10.5a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7L12.2 18.5" />
    </>
  ),
  copy: (
    <>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4.5a2 2 0 0 1-2-2V4.5a2 2 0 0 1 2-2H13a2 2 0 0 1 2 2V5" />
    </>
  ),
  'chat-empty': (
    <>
      <rect x="14" y="16" width="46" height="34" rx="6" />
      <path d="M26 30h22M26 38h14" />
      <path d="M60 40h14a4 4 0 0 1 4 4v14a4 4 0 0 1-4 4h-4v6l-7-6h-9a4 4 0 0 1-4-4v-6" />
    </>
  ),
};

export function G({
  n,
  size = 24,
  label,
  className,
}: {
  n: Glyph;
  size?: number;
  label?: string;
  className?: string;
}) {
  const viewBox = n === 'chat-empty' ? '0 0 92 78' : '0 0 24 24';
  return (
    <svg
      width={size}
      height={n === 'chat-empty' ? (size * 78) / 92 : size}
      viewBox={viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth={n === 'check' || n === 'check-double' ? 1.9 : 1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      focusable="false"
    >
      {label ? <title>{label}</title> : null}
      {P[n]}
    </svg>
  );
}
